import { apiOrigin } from '../utils/surface';

const SHOP_API = `${apiOrigin()}/stripe/accessory-shop/products`;
const PROFILES_API = `${apiOrigin()}/stripe/accessory-shop/device-profiles`;
const CHECKOUT_API = `${apiOrigin()}/stripe/accessory-shop`;

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('intrlock_token') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handle = async (res: Response) => {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `${res.status} ${res.statusText}`);
  }
  return res.json();
};

export interface AccessoryProduct {
  id: string;
  name: string;
  description: string;
  category: string;
  price_cents: number;
  image_url: string;
  stock: number | null;
  active: boolean;
  device_profile_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface DeviceProfileBomItem {
  sku: string;
  qty: number;
  label: string;
  location?: string;
}

export interface DeviceProfile {
  id: string;
  name: string;
  description?: string;
  role: string;
  has_cellular: boolean;
  default_role_config?: Record<string, unknown>;
  bom: DeviceProfileBomItem[];
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AccessoryOrderLine {
  productId: string;
  productTitle: string;
  unitPrice: number;
  quantity: number;
  imageSrc?: string;
}

export interface AccessoryOrderAddress {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address1: string;
  city: string;
  region: string;
  zip: string;
  country: string;
}

export interface ProvisionedDevice {
  serial: string;
  device_profile_id: string;
  device_profile_name: string;
  role: string;
  has_cellular: boolean;
  activation_code: string;
  install_token: string;
  ssh_public_key: string;
  ssh_private_key_secret_arn: string;
  iot_endpoint: string;
  iot_thing_name: string;
}

export interface AccessoryOrder {
  id: string;
  status: 'pending_payment' | 'paid_pending_approval' | 'approved' | 'shipped' | 'rejected';
  cart: AccessoryOrderLine[];
  address: AccessoryOrderAddress;
  customer_email: string;
  subtotal_cents: number;
  shipping_cents: number;
  total_cents: number;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  stripe_payment_status?: string;
  tracking_number?: string;
  carrier?: string;
  rejection_reason?: string;
  provisioned_devices?: ProvisionedDevice[];
  created_at: string;
  paid_at?: string;
  approved_at?: string;
  shipped_at?: string;
  rejected_at?: string;
}

export const deviceProfilesApi = {
  list: (): Promise<DeviceProfile[]> => fetch(PROFILES_API, { headers: authHeaders() }).then(handle),
  get: (id: string): Promise<DeviceProfile> => fetch(`${PROFILES_API}/${id}`, { headers: authHeaders() }).then(handle),
  create: (data: Partial<DeviceProfile>): Promise<DeviceProfile> =>
    fetch(PROFILES_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),
  update: (id: string, data: Partial<DeviceProfile>): Promise<DeviceProfile> =>
    fetch(`${PROFILES_API}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),
  remove: (id: string): Promise<{ deleted: true }> =>
    fetch(`${PROFILES_API}/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handle),
};

export const accessoryProductsApi = {
  listActive: (): Promise<AccessoryProduct[]> => fetch(SHOP_API).then(handle),
  listAll: (): Promise<AccessoryProduct[]> => fetch(`${SHOP_API}/all`, { headers: authHeaders() }).then(handle),
  get: (id: string): Promise<AccessoryProduct> => fetch(`${SHOP_API}/${id}`).then(handle),
  create: (data: Partial<AccessoryProduct>): Promise<AccessoryProduct> =>
    fetch(SHOP_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),
  update: (id: string, data: Partial<AccessoryProduct>): Promise<AccessoryProduct> =>
    fetch(`${SHOP_API}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),
  remove: (id: string): Promise<{ deleted: true }> =>
    fetch(`${SHOP_API}/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handle),
};

export const accessoryOrdersApi = {
  createCheckoutSession: (data: {
    cart: AccessoryOrderLine[];
    address: AccessoryOrderAddress;
    shipping_cents?: number;
    return_origin?: string;
  }): Promise<{ order_id: string; url: string; session_id: string }> =>
    fetch(`${CHECKOUT_API}/checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handle),

  syncSession: (session_id: string): Promise<AccessoryOrder> =>
    fetch(`${CHECKOUT_API}/sync-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id }),
    }).then(handle),

  listOrders: (): Promise<AccessoryOrder[]> =>
    fetch(`${CHECKOUT_API}/orders`, { headers: authHeaders() }).then(handle),

  approve: (id: string): Promise<AccessoryOrder> =>
    fetch(`${CHECKOUT_API}/orders/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: '{}',
    }).then(handle),

  ship: (id: string, tracking_number: string, carrier: string): Promise<AccessoryOrder> =>
    fetch(`${CHECKOUT_API}/orders/${id}/ship`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ tracking_number, carrier }),
    }).then(handle),

  reject: (id: string, reason: string): Promise<AccessoryOrder> =>
    fetch(`${CHECKOUT_API}/orders/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reason }),
    }).then(handle),
};
